import { createFileRoute, Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { motion } from 'framer-motion';
import { ChevronRight, Clock, MessageSquare } from 'lucide-react';
import { blogPosts } from "@/data/blog-posts";
import { trackWhatsAppClick } from "@/lib/gtm";

export const Route = createFileRoute("/blog/")({
  component: BlogIndexPage,
});

function BlogIndexPage() {
  return (
    <>
      <SEO 
        title="Blog STOP SOM | Janelas e esquadrias acústicas no RJ"
        description="Dicas sobre conforto acústico, janelas antirruído, esquadrias acústicas e soluções sob medida para ambientes no Rio de Janeiro."
      />
      <div className="bg-white">
      {/* Hero Interno */}
      <section className="pt-32 pb-12 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="container mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4">Blog STOP SOM</h1>
            <p className="text-lg md:text-xl opacity-90 leading-relaxed max-w-2xl mx-auto">
              Dicas sobre conforto acústico, janelas antirruído, esquadrias acústicas e soluções sob medida para ambientes no Rio de Janeiro.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Grid de Posts */}
      <section className="py-10 md:py-16 px-6">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {blogPosts.map((post, i) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="group flex flex-col bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all border border-gray-100"
              >
                <Link to={`/blog/${post.id}`} className="block relative aspect-video overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title} 
                    width="800"
                    height="450"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4 bg-accent text-primary text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                    {post.category}
                  </div>
                </Link>
                <div className="p-8 flex flex-col flex-grow text-left">
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-4">
                    <span className="flex items-center gap-1.5"><Clock size={14} /> {post.readingTime}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-4 text-primary group-hover:text-secondary transition-colors line-clamp-2">
                    <Link to={`/blog/${post.id}`}>{post.title}</Link>
                  </h3>
                  <p className="text-gray-500 mb-8 line-clamp-3 leading-relaxed text-sm md:text-base">
                    {post.excerpt}
                  </p>
                  <div className="mt-auto">
                    <Link 
                      to={`/blog/${post.id}`} 
                      className="inline-flex items-center gap-2 text-primary font-bold hover:text-secondary transition-colors"
                    >
                      Ler artigo <ChevronRight size={18} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final (WhatsApp) */}
      <section className="py-6 md:py-12 px-6 bg-white overflow-hidden">
        <div className="container mx-auto">
          <div className="bg-primary rounded-[2.5rem] md:rounded-[3.5rem] p-10 md:p-16 relative overflow-hidden text-center text-white">
            <div className="absolute top-0 right-0 w-1/3 h-full bg-accent/20 skew-x-12 translate-x-1/2" />
            <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-6">Precisa reduzir o barulho no seu ambiente?</h2>
              <p className="text-lg md:text-xl opacity-80 font-normal mb-10 leading-relaxed text-center">
                Fale com a STOP SOM Janelas Acústicas e receba orientação para escolher a melhor janela ou esquadria acústica para sua casa, apartamento, escritório ou empresa no Rio de Janeiro.
              </p>
              <div className="flex justify-center w-full">
                <a 
                  href="https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento."
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("Solicitar atendimento pelo WhatsApp", "https://wa.me/5521992698354?text=Ol%C3%A1%2C%20tudo%20bem%3F%20Estava%20no%20site%20e%20preciso%20de%20atendimento.", "blog")}
                  data-gtm-event="whatsapp_click"
                  data-conversion="whatsapp"
                  data-button-location="blog"
                  className="inline-flex items-center gap-2 bg-accent text-primary px-8 py-4 md:px-10 md:py-5 rounded-2xl font-bold hover:bg-white transition-all shadow-lg hover:shadow-xl text-center"
                >
                  Solicitar atendimento pelo WhatsApp <MessageSquare size={20} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
}
